import { ReactNode, createContext, useContext, useMemo, useState } from "react";

import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import AppBar from "decodable-ui/lib/components/AppBar";
import Content from "decodable-ui/lib/layouts/Content";

import { TextField } from "@mui/material";
import { useActiveAccount } from "../accounts/hooks/useAccounts";

const isLocal =
  typeof window != undefined &&
  window?.location?.origin.startsWith("http://localhost:");

export const featureFlagDefaults = {
  sqlAutocomplete: false,
  dataPlaneAPIs: true,
  dataPlaneApiBaseUrl: "",
  intercom: true,
  msc: true,
  lineage: true,
};

export type FeatureFlags = typeof featureFlagDefaults;
export type FeatureName = keyof FeatureFlags;

type CellOverrides = { [key: number]: Partial<FeatureFlags> };

// The standard cell overrides that should always be used in prod (as opposed
// to the cellOverrides parameters that are in some functions for testing).
// Overriding the experiment flags at a cell level enables phased rollout of
// changes. The overrides will all be empty when no such migration is in progress.
export const standardCellOverrides: { [key: number]: Partial<FeatureFlags> } = {
  // cell=1 group=prod cloud=aws region=us-west-2
  1: {},
  // cell=2 group=prod cloud=aws region=us-east-1
  2: {},
  // cell=7 group=prod cloud=aws region=us-west-2
  7: {},
  // cell=12 group=prod cloud=aws region=us-east-1
  12: {},
  // cell=15 group=prod cloud=aws region=eu-central-1
  15: {},
  // cell=17 group=prod cloud=aws region=us-west-2
  17: {},
  // cell=19 group=prod cloud=aws region=ap-southeast-2
  19: {},

  // Dev Cells that have the edges view
  5: {},
  16: {},
  10: {},
  14: {},
};

function useFlagsForCurrentCell({
  cellOverrides,
  globalDefaults,
}: {
  // Injectable for testing
  globalDefaults?: FeatureFlags;
  cellOverrides?: CellOverrides;
}): FeatureFlags {
  const { activeAccount } = useActiveAccount();

  const cellDefaults = useMemo(() => {
    try {
      if (!activeAccount) {
        return globalDefaults;
      }

      return {
        ...globalDefaults,
        ...cellOverrides[parseInt(activeAccount.cell)],
      };
    } catch (ex) {
      console.error(
        "Failed to load per-cell flag overrides. Using the defaults.",
        ex
      );

      return globalDefaults;
    }
  }, [activeAccount]);

  return cellDefaults;
}

export type ExperimentsContextValue = {
  flags: FeatureFlags;
  setFlag: (name: string, value: boolean | string) => void;
};

export const ExperimentsContext = createContext<ExperimentsContextValue>({
  flags: featureFlagDefaults,
  setFlag: () => {},
});

export function ExperimentsProvider({
  globalDefaults = featureFlagDefaults,
  cellOverrides = standardCellOverrides,
  children,
}: {
  // Injectable for testing
  globalDefaults?: FeatureFlags;
  cellOverrides?: CellOverrides;

  children: ReactNode;
}) {
  const cellFlags = useFlagsForCurrentCell({ cellOverrides, globalDefaults });

  const [sessionFlags, setSessionExperiments] = useState(() =>
    sessionStorage.experiments ? JSON.parse(sessionStorage.experiments) : null
  );

  const effectiveFlags = sessionFlags ?? cellFlags ?? featureFlagDefaults;

  const setFlag = (name: string, value: any) => {
    const updated = {
      ...effectiveFlags,
      [name]: value,
    };
    sessionStorage.experiments = JSON.stringify(updated);
    setSessionExperiments(updated);
  };

  return (
    <ExperimentsContext.Provider
      value={{
        flags: effectiveFlags,
        setFlag,
      }}
    >
      {children}
    </ExperimentsContext.Provider>
  );
}

export function useFeatureFlags(): ExperimentsContextValue {
  const ctx = useContext(ExperimentsContext);
  if (ctx == null) {
    return {
      flags: featureFlagDefaults,
      setFlag: () => {
        throw new Error("cannot setFlag outside of an ExperimentsProvider!");
      },
    };
  }
  return ctx;
}

export default ExperimentsPage;

export function ExperimentsPage() {
  const { flags, setFlag } = useFeatureFlags();

  const changeHandler = (flagName: FeatureName) => (val: boolean) =>
    setFlag(flagName, val);

  return (
    <>
      <AppBar anonymous />
      <Content>
        <Paper sx={{ padding: 3, display: "flex", flexDirection: "column" }}>
          <Flag
            label="Enable Data Plane APIs"
            value={!!flags?.dataPlaneAPIs}
            onChange={changeHandler("dataPlaneAPIs")}
          />
          {process.env.NODE_ENV != "production" && flags.dataPlaneAPIs ? (
            <TextField
              label="Data Plane API Base URL Override"
              variant="filled"
              placeholder="Default for local dev is: http://localhost:9090"
              value={flags.dataPlaneApiBaseUrl}
              onChange={(e) => setFlag("dataPlaneApiBaseUrl", e.target.value)}
            />
          ) : null}
          <Flag
            label="Enable SQL autocomplete"
            value={!!flags?.sqlAutocomplete}
            onChange={changeHandler("sqlAutocomplete")}
          />
          <Flag
            label="Enable Intercom"
            value={!!flags?.intercom}
            onChange={changeHandler("intercom")}
          />
          <Flag
            label="Enable MSC creation/update"
            value={!!flags?.msc}
            onChange={changeHandler("msc")}
          />
          <Flag
            label="Enable lineage"
            value={!!flags?.lineage}
            onChange={changeHandler("lineage")}
          />
        </Paper>
      </Content>
    </>
  );
}

function Flag({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (_: boolean) => void;
}) {
  return (
    <FormControlLabel
      control={<Checkbox checked={value} onChange={() => onChange(!value)} />}
      label={label}
    />
  );
}
